import React from "react";
import { AiOutlinePlaySquare } from "@react-icons/all-files/ai/AiOutlinePlaySquare";
import {
  AspectRatio,
  Box,
  Heading,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Link as GatsbyLink } from "gatsby";
import { ReactComponent as GraphOs } from "../images/icons/graphos.svg";

export type CardProps = {
  image?: string;
  title: string;
  description: string;
  url?: string;
  youtubeUrl?: string;
  video?: boolean;
  isAccent?: boolean;
  onClickEventName?: string;
};

type OtherProps = {
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
  as: any;
};

const Card = ({
  image,
  title,
  description,
  url,
  youtubeUrl,
  video,
  isAccent,
  onClickEventName,
}: CardProps) => {
  const CARD_STYLE = {
    borderColor: "silver.400",
    titleColor: "gray.900",
    color: "gray.500",
    bg: "white",
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  const isExternal = url?.includes("http");
  const isApolloProperty =
    url?.includes("https://www.apollographql.com") ||
    url?.includes("https://www.summit.graphql.com");

  let otherProps: OtherProps = {
    to: url,
    as: GatsbyLink,
  };

  if (isExternal) {
    otherProps = {
      as: Link,
      href: url,
      target: "_blank",
      rel: isApolloProperty ? "noopener" : "noopener noreferrer",
    };
  }

  return (
    <>
      <Link
        onClick={() => {
          if (onClickEventName) {
            window.gtag?.("event", onClickEventName, {
              title,
            });
          }
        }}
        borderStyle="solid"
        borderColor={CARD_STYLE.borderColor}
        bg={CARD_STYLE.bg}
        borderWidth="1px"
        borderRadius="12px"
        pos="relative"
        overflow="hidden"
        gridColumn={isAccent ? "span 2/3" : "auto"}
        _hover={{
          textDecoration: "none",
          transform: "translateY(-2px)",
          transition: "all 0.1s ease-in-out",
          boxShadow: "0 6px 6px -6px rgba(0,0,0,0.2)",
        }}
        role="group"
        {...otherProps}
      >
        {image && (
          <Box
            borderWidth="0 0 1px 0"
            borderStyle="solid"
            borderColor="silver.500"
            p={video || image ? "0" : "6"}
            textAlign="center"
            bg="gray.50"
            position="relative"
            overflow="hidden"
          >
            <Image
              minW={video ? "100%" : "auto"}
              maxH={video ? "auto" : "100%"}
              maxW="100%"
              margin="0 auto"
              src={image}
              top="-50%"
              pos="relative"
            />
          </Box>
        )}
        <Box
          onClick={() => {
            if (video) onOpen();
          }}
          display="flex"
          alignItems="center"
          textAlign="left"
          p="6"
        >
          {isAccent && (
            <Box mr="30px">
              <GraphOs />
            </Box>
          )}
          <Box>
            <Heading
              as="h3"
              variant="heading3"
              alignItems="top"
              display="flex"
              mb="2"
              color="gray.800"
              justifyContent="space-between"
            >
              {title}{" "}
              {video && (
                <Box ml="2">
                  <AiOutlinePlaySquare color="inherit" size="24" />
                </Box>
              )}
            </Heading>
            <Text color={CARD_STYLE.color}>{description}</Text>
          </Box>
        </Box>
      </Link>
      {video && (
        <Modal returnFocusOnClose={false} isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent maxW="5xl" p="5">
            <ModalCloseButton />
            <ModalBody>
              <AspectRatio ratio={16 / 9}>
                <iframe title={title} src={youtubeUrl} allowFullScreen />
              </AspectRatio>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default Card;
